import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Dashboard.css';

const generalInstructions = {
  title: 'General Instructions (Applies To All Games)',
  items: [
    'From the Dashboard, click any game you want to play (WHOT, Chess, Checkers, Draughts, Flappy Duel, Color Arena, or Dice).',
    'Each game lobby provides three main modes: Play vs Bot, Friendly Match, and Real Match (Wager/Bet).',
    'To create a challenge, choose your mode, set your game options, create the room, and wait for another player to join.',
    'After creating a room, copy the game link and share it with your friends on any platform to invite them quickly.',
    'Stay online and ready after creating challenges. Missed turns can trigger penalties depending on the game rules.',
    'Use a stable network connection during matches to avoid timeout losses and turn penalties.',
    'Use Practice Mode or AI/Bot mode first to learn controls and game flow before entering real wager matches.',
    'You can only create up to three active games at a time; you may need to finish ongoing games before creating another one.',
    'Make sure you have enough WikiPenny in your wallet before creating any real bet game.',
    'Minimum wager per real game is 50 WikiPenny.',
    'Wallet conversion reference: 100 WKP = 1 USD.',
    'Minimum deposit and minimum withdrawal are both 2 USDT (no extra platform charge on deposit/withdrawal processing).',
    'For real bet games, wager amounts are held in escrow once players join.',
    'Platform charge is currently 8% of total pot (configurable by admin/community settings).',
    'After platform charge deduction, remaining pool is distributed using the active sharing formula.',
    'Sharing formula is dynamic and can be updated by the community/admin in platform settings (database).',
    'Current cautious sharing model (subject to change): 2 players = 90:10, 3 players = 65:25:10, 4 players = 60:25:10:5, 5 players = 60:25:10:4:1.',
    'After the game, system ranking determines winner/order and applies the active formula for settlement automatically.',
    'Game invite links are shareable on any platform (WhatsApp, Telegram, X, Facebook, etc.) and can be sent to any player.',
    'When someone opens a game link: if a player slot is still open, they can join the room; if the room is already full, they are placed in watch-only spectator mode.'
  ],
  formulas: [
    'totalPot = wager x totalPlayers',
    'platformCharge = totalPot x 8% (or current configured charge)',
    'netPot = totalPot - platformCharge',
    'payoutPerRank = netPot x rankRatio (based on active sharing formula)'
  ]
};

const guideSections = [
  {
    id: 'whot',
    title: 'WHOT',
    icon: '🃏',
    gradient: 'from-purple-500 to-pink-500',
    objective: 'Be the first player to finish all your cards, or survive longest in multi-player elimination rooms.',
    howToPlay: [
      'Match cards by shape, color, or number based on the top card on the pile.',
      'Time control is active per turn. If your turn timer expires before a valid move, you forfeit the turn and penalty cards are added to your hand.',
      'Missed-turn penalties are tracked. If you lose turn by timeout 3 times in one match, you lose that match automatically.',
      'If you have no matching card, draw from market according to the room rules and continue play to the next player.',
      'Special command cards (Pick 2, Hold On, Suspension, General Market, WHOT and any enabled house-rule commands) apply immediately when played.',
      'In 3 to 4 player mode, if one player is knocked out, that player is fixed to last position automatically while remaining players continue until final winner is decided.',
      'Room creator can deactivate house rules and disable command cards they do not want before creating the game.'
    ],
    winningTips: [
      'Save powerful action cards for key moments.',
      'Track what shapes/colors opponents are likely to need.',
      'Avoid exposing yourself to counter-action chains.',
      'Play before timeout consistently to avoid auto-loss from 3 missed turns.'
    ],
    commandCards: [
      'Pick 2: Next eligible player draws two cards unless counter-rules in your room allow stacking/response.',
      'General Market: Forces market draw as defined by active house rules.',
      'Hold On: Keeps play pressure by controlling immediate flow/continuation based on enabled rule behavior.',
      'Suspension: Skips/suspends the next player turn under active room rules.',
      'WHOT (20): Wild-command card that can change play direction/target depending on room configuration.'
    ]
  },
  {
    id: 'chess',
    title: 'Chess',
    icon: '♟️',
    gradient: 'from-blue-500 to-indigo-500',
    objective: 'Checkmate your opponent\'s king.',
    howToPlay: [
      'Open Dashboard > Chess, then choose Bot, Friendly, or Real Match.',
      'Create challenge and wait in lobby, or join with room link/code.',
      'White moves first, then turns alternate through the whole game.',
      'Piece movement rules: king (1 square), queen (any direction), rook (straight), bishop (diagonal), knight (L-shape), pawn (forward with diagonal capture).',
      'You cannot make a move that leaves your king in check; illegal moves are rejected by game engine.'
    ],
    turnRules: [
      'Turn timer is active. If you do not move in time, timeout handling applies according to active room rules.',
      'Repeated inactivity can convert to loss/forfeit depending on challenge mode and server settings.',
      'Keep stable internet to avoid accidental timeout losses.'
    ],
    winningTips: [
      'Control the center early (e4/d4/e5/d5 squares).',
      'Castle early for king safety.',
      'Trade when ahead in material; keep complexity when behind.'
    ]
  },
  {
    id: 'checkers',
    title: 'Checkers',
    icon: '🔴',
    gradient: 'from-red-500 to-orange-500',
    objective: 'Capture all opponent pieces or leave them with no legal moves.',
    howToPlay: [
      'Open Dashboard > Checkers and select preferred mode.',
      'Create room, copy invite link if needed, and wait for opponent.',
      'Pieces move diagonally forward on dark squares.',
      'Capture by jumping over adjacent opponent piece into empty square.',
      'When a piece reaches far row, it promotes to king and can move in both directions.',
      'Multiple captures can chain in one turn when available.'
    ],
    turnRules: [
      'Your turn is clearly marked with in-game turn indicator and sound toggle option.',
      'If timer expires without move, timeout penalties/forfeit rules can apply.',
      'Do not leave match after creating challenge; inactivity can affect outcome.'
    ],
    winningTips: [
      'Maintain piece structure and avoid isolated pieces.',
      'Force exchanges when ahead.',
      'Promote to kings early to control both directions.'
    ]
  },
  {
    id: 'draughts',
    title: 'Draughts',
    icon: '⚫',
    gradient: 'from-gray-700 to-gray-900',
    objective: 'Outplay your opponent by tactical captures and king control.',
    howToPlay: [
      'Go to Dashboard > Draughts, choose mode, then create or join a room.',
      'Share room link from lobby if inviting others directly.',
      'Move diagonally and capture by jumping opponent pieces.',
      'Forced or prioritized captures apply based on active draughts rule set.',
      'Promotion happens at far row and kings gain wider movement.'
    ],
    turnRules: [
      'Turn countdown applies; missing turn can trigger timeout handling.',
      'Turn alerts are available in UI with sound toggle for clarity.',
      'For long tactical turns, plan quickly to avoid penalty from timer expiry.'
    ],
    winningTips: [
      'Think ahead for forced capture sequences.',
      'Keep center control and avoid edge traps.',
      'Use kings to dominate long diagonals.'
    ]
  },
  {
    id: 'flappy',
    title: 'Flappy Duel',
    icon: '🐦',
    gradient: 'from-green-500 to-teal-500',
    objective: 'Score higher than your opponent in your run/turn window.',
    howToPlay: [
      'Open Dashboard > Flappy Duel, pick mode, then create/join room.',
      'Tap or click to flap through pipes and avoid obstacles.',
      'Score increases by surviving and clearing more pipes.',
      'In duel challenge, each player submits score under match timing constraints.',
      'Highest valid final score wins the match.'
    ],
    turnRules: [
      'Each run has timing/session constraints based on room settings.',
      'Late start or inactivity can count as poor/zero run depending on server rules.',
      'Ensure stable network before starting run to avoid sync issues.'
    ],
    winningTips: [
      'Use short controlled taps to stabilize altitude.',
      'Focus on rhythm over speed.',
      'Do not over-correct after near misses.'
    ]
  },
  {
    id: 'reaction',
    title: 'Color Arena',
    icon: '⚡',
    gradient: 'from-yellow-500 to-red-500',
    objective: 'React correctly and faster than your opponent.',
    howToPlay: [
      'Go to Dashboard > Color Arena and choose mode.',
      'Create room and wait, or open invite link to join directly.',
      'Follow prompt color/pattern instantly and tap correct response.',
      'Wrong taps or delayed reactions reduce your score for that round.',
      'Final winner is based on total speed + accuracy performance.'
    ],
    turnRules: [
      'Reaction rounds are time-sensitive; delayed responses are penalized.',
      'If you are inactive or disconnected during active round, score can drop significantly.',
      'Use low-latency network for fair reaction timing.'
    ],
    winningTips: [
      'Prioritize accuracy before pure speed.',
      'Keep your eyes centered and reduce distractions.',
      'Build consistency over burst reactions.'
    ]
  },
  {
    id: 'dice',
    title: 'Dice',
    icon: '🎲',
    gradient: 'from-indigo-500 to-purple-500',
    objective: 'Outscore opponents according to the room\'s dice rules and turn sequence.',
    howToPlay: [
      'Open Dashboard > Dice and join/create a room.',
      'Wait until required players are ready.',
      'Roll only on your turn and follow the active scoring rule set.',
      'Some rounds reward totals, others reward specific target outcomes.',
      'Track standings and adjust risk across rounds.'
    ],
    turnRules: [
      'Turn timer applies. Missing your turn can reduce your competitiveness or trigger penalties.',
      'Inactivity/disconnect during round may be treated as skipped turn by server.',
      'Be online and attentive before entering real wager dice rooms.'
    ],
    winningTips: [
      'Learn your room\'s exact scoring rule before committing.',
      'Track opponents\' scores and adapt your risk level.',
      'Use safe scoring when leading.'
    ]
  }
];

export default function GameGuide() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="dashboard-content">
        {/* Header */}
        <div className="guide-header">
          <div className="header-left">
            <div className="header-icon guide-icon-main">📘</div>
            <div>
              <h1 className="page-title">Game Guide</h1>
              <p className="page-subtitle">Complete rules, strategies, and platform information</p>
            </div>
          </div>
          <button className="back-btn" onClick={() => navigate(user ? '/dashboard' : '/login')}>
            {user ? '← Back to Dashboard' : '← Back to Login'}
          </button>
        </div>

        {/* Quick Navigation Chips */}
        <div className="guide-nav">
          <a href="#general" className="nav-chip nav-chip-highlight">
            <span className="chip-icon">📘</span>
            <span>General Rules</span>
          </a>
          {guideSections.map((section) => (
            <a key={section.id} href={`#${section.id}`} className="nav-chip">
              <span className="chip-icon">{section.icon}</span>
              <span>{section.title}</span>
            </a>
          ))}
        </div>

        {/* General Section */}
        <div id="general" className="guide-card general-card">
          <div className="card-header">
            <div className="card-icon">📘</div>
            <div>
              <h2 className="card-title">{generalInstructions.title}</h2>
              <p className="card-subtitle">Fundamental rules that apply across all games</p>
            </div>
          </div>

          <div className="guide-content">
            <div className="guide-block">
              <h3 className="block-title">📋 Core Platform Rules</h3>
              <ul className="guide-list">
                {generalInstructions.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="guide-block formula-block">
              <h3 className="block-title">💰 Settlement Formula</h3>
              <div className="formula-grid">
                {generalInstructions.formulas.map((formula, index) => (
                  <code key={index} className="formula-code">{formula}</code>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Game Sections Grid */}
        <div className="guide-grid">
          {guideSections.map((section) => (
            <div key={section.id} id={section.id} className="guide-card">
              <div className="card-header">
                <div className="card-icon" style={{ background: `linear-gradient(135deg, var(--gradient-start), var(--gradient-end))` }}>
                  {section.icon}
                </div>
                <h2 className="card-title">{section.title}</h2>
              </div>

              <div className="guide-content">
                {/* Objective */}
                <div className="guide-block compact">
                  <h3 className="block-title">🎯 Objective</h3>
                  <p className="block-text">{section.objective}</p>
                </div>

                {/* How To Play */}
                <div className="guide-block">
                  <h3 className="block-title">🎮 How To Play</h3>
                  <ul className="guide-list">
                    {section.howToPlay.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>

                {/* Turn Rules */}
                {section.turnRules && (
                  <div className="guide-block">
                    <h3 className="block-title">⏱️ Turn & Timeout Rules</h3>
                    <ul className="guide-list">
                      {section.turnRules.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Winning Tips */}
                <div className="guide-block tips-block">
                  <h3 className="block-title">💡 Winning Tips</h3>
                  <ul className="guide-list tips-list">
                    {section.winningTips.map((item, idx) => (
                      <li key={idx}>✨ {item}</li>
                    ))}
                  </ul>
                </div>

                {/* Command Cards (WHOT specific) */}
                {section.commandCards && (
                  <div className="guide-block command-block">
                    <h3 className="block-title">🃏 Command Cards</h3>
                    <ul className="guide-list">
                      {section.commandCards.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        <div className="guide-footer">
          <div className="footer-content">
            <span className="footer-icon">ℹ️</span>
            <span>Rules and formulas are subject to change based on community voting and admin settings. Check the platform settings for current values.</span>
          </div>
        </div>
      </div>
    </div>
  );
}